import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Divider, IconButton,
  Tooltip, Typography, Alert, CircularProgress,
  Box,
  Chip,
} from '@mui/material'
import { SearchBar } from '../../components/ui'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Shield as ShieldIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  VpnKey as KeyIcon,
  Business as OrgIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react-lite'
import { PageHeader } from '../../components/shared/PageHeader'
import { PageActionButtons } from '../../components/shared/PageActionButtons'
import { DeleteDialog } from '../../components/shared/DeleteDialog'
import { rolesApi, Role } from '../../lib/api/rolesApi'
import { orgsApi } from '../../lib/api/tenantsApi'
import { themeColors } from '../../app/layout/theme'
import { authStore } from '../../lib/stores/authStore'

// ── Main Page ─────────────────────────────────────────────────────────────────
const RolesPage = observer(function RolesPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const isSuperAdmin = authStore.user?.roleName === 'SUPER_ADMIN'

  const [search, setSearch] = useState('')
  const [deletingRole, setDeletingRole] = useState<Role | null>(null)

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesApi.getAll,
  })

  const { data: orgs = [] } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  })

  // Build a quick id→name map for org badge labels
  const orgNameMap = useMemo(() =>
    Object.fromEntries(orgs.map(o => [o.id, o.name])),
    [orgs]
  )

  const deleteMutation = useMutation({
    mutationFn: (id: string) => rolesApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setDeletingRole(null); toast.success('Role deleted') },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete role'),
  })

  const filtered = useMemo(() =>
    roles.filter((r) =>
      !search || r.name.toLowerCase().includes(search.toLowerCase()) ||
      (r.description ?? '').toLowerCase().includes(search.toLowerCase())
    ), [roles, search])

  const globalRoles = filtered.filter(r => r.orgId === null)
  const orgRoles = filtered.filter(r => r.orgId !== null)

  const renderGrid = (list: Role[]) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
      {list.map((role) => (
        <RoleCard
          key={role.id}
          role={role}
          orgName={role.orgId ? (orgNameMap[role.orgId] ?? 'Org') : null}
          onEdit={() => navigate(`/admin/roles/${role.id}`)}
          onDelete={() => setDeletingRole(role)}
        />
      ))}
    </Box>
  )

  return (
    <Box>
      <PageHeader
        title="Role Management"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
        actions={
          <PageActionButtons
            onBulkUpload={() => toast.info('Bulk upload coming soon')}
            onNew={() => navigate('/admin/roles/new')}
            newLabel="New Role"
          />
        }
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 2.5, maxWidth: 400 }}>
          <SearchBar
            placeholder="Search roles..."
            value={search}
            onChange={setSearch}
          />
        </Box>

        {rolesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center', color: themeColors.text.secondary }}>
            <ShieldIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
            <Typography>No roles found</Typography>
          </Box>
        ) : (
          <>
            {/* Global / system roles */}
            {globalRoles.length > 0 && (
              <Box sx={{ mb: orgRoles.length > 0 ? 4 : 0 }}>
                <Typography sx={{
                  fontSize: '11px', fontWeight: 700, color: themeColors.text.muted,
                  textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.5
                }}>
                  Global Roles
                </Typography>
                {renderGrid(globalRoles)}
              </Box>
            )}

            {/* Org-specific roles */}
            {orgRoles.length > 0 && (
              <Box>
                <Typography sx={{
                  fontSize: '11px', fontWeight: 700, color: themeColors.text.muted,
                  textTransform: 'uppercase', letterSpacing: '0.8px', mb: 1.5
                }}>
                  Organisation Roles
                </Typography>
                {renderGrid(orgRoles)}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Delete Dialog */}
      <DeleteDialog
        open={!!deletingRole}
        title="Delete Role"
        name={deletingRole?.name ? `the ${deletingRole.name} role` : undefined}
        onClose={() => setDeletingRole(null)}
        onConfirm={() => deletingRole && deleteMutation.mutate(deletingRole.id)}
        isDeleting={deleteMutation.isPending}
        confirmDisabled={(deletingRole?.count?.users ?? 0) > 0}
      >
        {deletingRole && (deletingRole.count?.users ?? 0) > 0 && (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', fontSize: '13px' }}>
            This role has <strong>{deletingRole.count?.users} user(s)</strong> assigned. Reassign them before deleting.
          </Alert>
        )}
      </DeleteDialog>
    </Box>
  )
})

export default RolesPage

// ── Role Card ─────────────────────────────────────────────────────────────────
function RoleCard({ role, orgName, onEdit, onDelete }: {
  role: Role; orgName: string | null; onEdit: () => void; onDelete: () => void
}) {
  const isSuperAdmin = role.name === 'SUPER_ADMIN'
  const isOrgRole = role.orgId !== null
  const permCount = role.permissions.length

  // Color per role
  const roleColor =
    isSuperAdmin ? themeColors.error :
      isOrgRole ? '#0e7c7b' :
        role.name === 'ADMIN' ? '#7c3aed' :
          role.name === 'MANAGER' ? '#0070d2' :
            themeColors.text.secondary

  return (
    <Box
      sx={{
        backgroundColor: themeColors.surface,
        border: `1px solid ${themeColors.border}`,
        borderRadius: '12px',
        p: 2.5,
        boxShadow: themeColors.shadow.sm,
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': { boxShadow: themeColors.shadow.md, transform: 'translateY(-1px)' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: '10px',
              backgroundColor: `${roleColor}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <ShieldIcon sx={{ fontSize: 20, color: roleColor }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: themeColors.text.primary, letterSpacing: '0.3px' }}>
              {role.name}
            </Typography>
            {isSuperAdmin && (
              <Chip label="System Role" size="small" sx={{ height: 18, fontSize: '10px', fontWeight: 600, bgcolor: `${themeColors.error}15`, color: themeColors.error, mt: '2px' }} />
            )}
            {isOrgRole && orgName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: '2px' }}>
                <OrgIcon sx={{ fontSize: 11, color: '#0e7c7b' }} />
                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#0e7c7b' }}>{orgName}</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit role">
            <IconButton size="small" onClick={onEdit} sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isSuperAdmin ? 'Cannot delete system role' : 'Delete role'}>
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={isSuperAdmin}
                sx={{ color: themeColors.text.secondary, '&:hover': { color: themeColors.error, bgcolor: `${themeColors.error}12` } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Description */}
      <Typography sx={{ fontSize: '12.5px', color: themeColors.text.secondary, mb: 2, minHeight: 20 }}>
        {role.description || <em style={{ opacity: 0.5 }}>No description</em>}
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <KeyIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
            <strong>{permCount}</strong> permission{permCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <PeopleIcon sx={{ fontSize: 14, color: themeColors.text.muted }} />
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
            <strong>{role.count?.users ?? 0}</strong> user{(role.count?.users ?? 0) !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Permission chips (first 4) */}
      {permCount > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
          {role.permissions.slice(0, 4).map(({ permission }) => (
            <Chip
              key={permission.id}
              label={permission.action}
              size="small"
              sx={{ height: 20, fontSize: '10.5px', bgcolor: themeColors.primaryLight, color: '#ffffff', fontWeight: 500 }}
            />
          ))}
          {permCount > 4 && (
            <Chip
              label={`+${permCount - 4} more`}
              size="small"
              sx={{ height: 20, fontSize: '10.5px', bgcolor: '#f0f0f0', color: themeColors.text.secondary }}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
